/**
 * Flex Message builders for LIFF share-to-chat (bead lucky-war-trko).
 *
 * PO decision 2026-09-04: sharing sends a native LINE **Flex Message** built
 * from card data — not a rasterized card image. No capture library, no
 * server-side image hosting; the payload is small JSON assembled client-side
 * and delivered via `liff.sendMessages()` (chat context) or
 * `liff.shareTargetPicker()` (fallback).
 *
 * STRUCTURE (v5-deeplink; Flex body device-bisected 2026-09-04): BODY-ONLY
 * bubble — title/scope are plain text rows. The LIFF share validator
 * (message/v3/share) rejects the BUTTON component even as a plain
 * {style:'link', uri action} — every on-device variant containing a button
 * failed with INVALID_MESSAGE while the button-free nested body passed,
 * despite the payload validating 0-error against LINE's public OpenAPI and
 * the documented "Flex (URI actions only)" rule. Do not reintroduce buttons,
 * headers, footers, or separators without re-verifying on device.
 *
 * Design rules:
 *  - Builders are pure functions of card data (unit-testable, no LIFF import).
 *  - All labels Thai (AGENTS.md locale conventions). altText ≤ 400 chars
 *    (LINE's documented limit) and doubles as the notification preview.
 *  - Honesty rules carry over: `windowLabel` must name the exact data window
 *    the numbers come from (e.g. "7 วันล่าสุด (25–31 ส.ค.)"), matching the
 *    card's own sub-labels.
 */

/** Minimal Flex Message component subset — only verified-accepted pieces. */
export interface FlexText {
  type: 'text';
  text: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
  weight?: 'regular' | 'bold';
  color?: string;
  wrap?: boolean;
  align?: 'start' | 'center' | 'end';
  flex?: number;
}

export interface FlexBox {
  type: 'box';
  layout: 'vertical' | 'horizontal';
  contents: FlexComponent[];
  spacing?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
}

export type FlexComponent = FlexText | FlexBox;

export interface FlexBubble {
  type: 'bubble';
  body?: FlexBox;
}

/** A complete Flex message payload ready for `liff.sendMessages`. */
export interface ShareFlex {
  altText: string;
  contents: FlexBubble;
}

/** Delta chip: `good` decides the color (green/red) — polarity is the card's call. */
export interface KpiDelta {
  text: string;
  good: boolean;
}

/** One metric line in the shared bubble. */
export interface KpiRow {
  label: string;
  value: string;
  delta?: KpiDelta;
}

/** Everything a KPI card contributes to its shared bubble. */
export interface KpiCardShareInput {
  /** Card title, e.g. "ยอดขายตอนนี้". */
  title: string;
  /** Scope line under the title, e.g. "สุกี้ · ทุกสาขา" or a branch name. */
  scope?: string;
  /** Exact data window (honesty rule — copy the card's own sub-label). */
  windowLabel?: string;
  rows: KpiRow[];
  /** Optional footnote (e.g. "ไม่รวม VAT"). */
  note?: string;
  /** SPA deep link — sent separately as a tappable LIFF URL in a text message. */
  linkPath?: string;
}

// Palette — mirrors the app theme; LINE adjusts for its dark mode.
const BRAND_RED = '#AE2C1A';
const COLOR_GOOD = '#1A7F37';
const COLOR_BAD = '#B42318';
const COLOR_MUTED = '#888888';
const COLOR_FAINT = '#999999';

/** Bumped on payload changes; surfaced in share diagnostics for stale-bundle detection. */
export const SHARE_SCHEMA_VERSION = 'v5-deeplink';

/** LINE caps altText at 400 characters. */
export const ALT_TEXT_MAX = 400;

function truncateAltText(s: string): string {
  if (s.length <= ALT_TEXT_MAX) return s;
  return s.slice(0, ALT_TEXT_MAX - 1) + '…';
}

/** Builds the plain-text summary used as altText (notification preview). */
export function buildAltText(input: KpiCardShareInput): string {
  const parts: string[] = [`【Lucky War】${input.title}`];
  if (input.scope) parts.push(input.scope);
  for (const row of input.rows.slice(0, 3)) {
    let line = `${row.label} ${row.value}`;
    if (row.delta) line += ` (${row.delta.text})`;
    parts.push(line);
  }
  if (input.windowLabel) parts.push(input.windowLabel);
  return truncateAltText(parts.join(' · '));
}

/** Builds the KPI share bubble — body-only (see file header). */
export function buildKpiFlex(input: KpiCardShareInput): ShareFlex {
  const bodyContents: FlexComponent[] = [
    { type: 'text', text: 'LUCKY WAR', size: 'xs', weight: 'bold', color: BRAND_RED },
    { type: 'text', text: input.title, size: 'lg', weight: 'bold', wrap: true },
  ];
  if (input.scope) {
    bodyContents.push({ type: 'text', text: input.scope, size: 'xs', color: COLOR_MUTED, wrap: true });
  }

  // Metric rows — the exact nested structure verified accepted on device.
  for (const row of input.rows) {
    const line: FlexComponent[] = [
      {
        type: 'box',
        layout: 'horizontal',
        spacing: 'sm',
        contents: [
          { type: 'text', text: row.label, flex: 1, size: 'sm', color: COLOR_MUTED, wrap: true },
          { type: 'text', text: row.value, size: 'md', weight: 'bold', wrap: true },
        ],
      },
    ];
    if (row.delta) {
      line.push({
        type: 'text',
        text: row.delta.text,
        size: 'xs',
        align: 'end',
        wrap: true,
        color: row.delta.good ? COLOR_GOOD : COLOR_BAD,
      });
    }
    bodyContents.push({ type: 'box', layout: 'vertical', spacing: 'xs', contents: line });
  }

  if (input.note) {
    bodyContents.push({ type: 'text', text: input.note, size: 'xs', color: COLOR_MUTED, wrap: true });
  }
  if (input.windowLabel) {
    bodyContents.push({ type: 'text', text: input.windowLabel, size: 'xs', color: COLOR_FAINT, wrap: true });
  }
  return {
    altText: buildAltText(input),
    contents: {
      type: 'bubble',
      body: { type: 'box', layout: 'vertical', spacing: 'md', contents: bodyContents },
    },
  };
}
